import { VIRTUAL_USER_GAP_CODE, VIRTUAL_USER_GAP_SEVERITY } from '@demandproai/shared';
import { EASY_MODE_STEP_LABELS, EASY_MODE_STEP_ORDER } from '../constants/easyModeSteps';

// Gaps that need a named party edited or removed: the easy-mode Case Intake step only carries party
// counts, so these route to the classic case editor where per-party edit and delete already live.
const PARTY_EDIT_GAP_CODES = new Set([
  VIRTUAL_USER_GAP_CODE.PARTY_COUNT_MISMATCH,
  VIRTUAL_USER_GAP_CODE.CLIENT_NAME_MISSING,
  VIRTUAL_USER_GAP_CODE.DEFENDANT_COVERAGE_MISSING
]);

// blocking first: a reader scanning the panel needs the stopper before the advice
const SEVERITY_ORDER = [
  VIRTUAL_USER_GAP_SEVERITY.BLOCKING,
  VIRTUAL_USER_GAP_SEVERITY.WARNING,
  VIRTUAL_USER_GAP_SEVERITY.INFO
];

const getSeverityRank = (severity) => {
  const rank = SEVERITY_ORDER.indexOf(severity);

  return rank === -1 ? SEVERITY_ORDER.length : rank;
};

const getStepLink = ({ caseId, targetStep, code }) => {
  if (!caseId) return null;

  // a party gap points at the classic editor, the only surface that can edit or remove a named party
  if (PARTY_EDIT_GAP_CODES.has(code)) {
    return { label: 'Case Editor', url: `/case/edit/${caseId}` };
  }

  if (!EASY_MODE_STEP_ORDER.includes(targetStep)) return null;

  return { label: EASY_MODE_STEP_LABELS[targetStep], url: `/case/easy/${caseId}?step=${targetStep}` };
};

export const getIsGapBlocking = ({ severity }) => severity === VIRTUAL_USER_GAP_SEVERITY.BLOCKING;

/**
 * Turns the agent's stored gap report into rows a panel can render.
 * @param {{ gapReport: Array, caseId: string }} params - the persisted report and the case it belongs to
 * @returns {Array<{ key, severity, title, detail, isBlocking, stepLink }>}
 */
export const buildGapReviewRows = ({ gapReport = [], caseId }) =>
  [...gapReport]
    .sort((left, right) => getSeverityRank(left.severity) - getSeverityRank(right.severity))
    .map((gap, index) => ({
      key: `${gap.code}-${index}`,
      severity: gap.severity,
      title: gap.title,
      detail: gap.detail,
      isBlocking: getIsGapBlocking(gap),
      stepLink: getStepLink({ caseId, targetStep: gap.targetStep, code: gap.code })
    }));

// The report is written once when the run ends, so an edit made afterwards is not reflected in it
export const buildGapReviewSummary = ({ gapReport = [] }) => {
  const blockingCount = gapReport.filter(getIsGapBlocking).length;

  if (blockingCount) {
    return `${blockingCount} item(s) must be resolved before a demand letter can be generated.`;
  }

  return 'Nothing is blocking the demand letter. These are worth a look before you send it.';
};
